/* eslint-disable react-hooks/exhaustive-deps */
import './index.less';

import { useColorMode } from '@tonic-one/react';
import { useCombobox } from 'downshift';
import React, { useEffect, useRef } from 'react';
import { LogApi } from '@Apis';
import { Criterion, SearchOptionItem } from 'src/types/log';

import { mappingFields, timeFields } from '../constants';
import { useLogReducerContext } from '../context';
import {
  getQuarantineL10nObj,
  unwrapSearchFieldFuncMap,
  wrapSearchFieldFuncMap,
} from '../shared';
import { useLogFields } from '../hooks';
import { MappingField, TimeField } from '../type';
import { menuMultipleStyles } from './const';
import { escapeQueryString, surroundWildcardForAscii } from '@Utils/chart';
import axios from 'axios';

function ComboBoxInput({
  item: editItem,
  inputHandler,
  setFocus: setCriteriaFocus,
  onComplete,
  style,
}: {
  item: Criterion;
  inputHandler: Function;
  setFocus: Function;
  onComplete: Function;
  style?: {};
}) {
  const [colorMode] = useColorMode();
  const abortControllerRef = useRef(new AbortController());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getItemsFilter(inputValue = '') {
    return function itemFilter(item: any) {
      return (
        !inputValue ||
        item.title.toLowerCase().includes(inputValue) ||
        item.key.toLowerCase().includes(inputValue)
      );
    };
  }
  const { label, value, focus, field } = editItem;
  const { state } = useLogReducerContext();
  const { type: logType, logs, timeRange } = state;
  const { wrapFields, mergedFields } = useLogFields(logType);
  const mergedSubFields = mergedFields[field] || [];

  const logFieldArr = React.useMemo((): string[] => {
    const logFieldArr: string[] = [];
    logs.forEach((log) => {
      Object.keys(log).forEach((key) => {
        if (key === field) {
          if (!logFieldArr.includes(log[key])) {
            logFieldArr.push(log[key]);
          }
        }
      });
    });
    return logFieldArr;
  }, [logs, field]);
  const stateReducer = React.useCallback(
    (state: any, actionAndChanges: any) => {
      const { type, changes } = actionAndChanges;
      // console.log('singleCriteria reducer:', type, changes);
      switch (type) {
        case useCombobox.stateChangeTypes.InputFocus:
        case useCombobox.stateChangeTypes.InputChange:
          return changes;
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: false, highlightedIndex: 0 }),
          };
        default:
          return changes; // otherwise business as usual.
      }
    },
    []
  );
  const [items, setItems] = React.useState<SearchOptionItem[]>([]);

  const quarantineL10nMap = React.useMemo(() => getQuarantineL10nObj(), []);
  const [width, setWidth] = React.useState(getTextWidth(value));
  const refInput = React.useRef<HTMLInputElement>(null);
  const setFocus = React.useCallback(
    (isFocus: boolean) => {
      if (isFocus) {
        refInput.current?.focus();
      }
      setCriteriaFocus(isFocus);
    },
    [setCriteriaFocus]
  );
  const autoComplete = React.useCallback(
    async (inputValue: string) => {
      if (inputValue.length <= 2) {
        return;
      }
      if (field === 'all') {
        return;
      }
      let tempResult: string[] = [];
      let result: SearchOptionItem[] = [];
      const fields = mergedSubFields.length ? mergedSubFields : [field];
      if (field === 'all' || timeFields.includes(field as TimeField)) {
        setItems([]);
      }
      fields.forEach((oneField) => {
        if (
          wrapFields.includes(oneField) &&
          Object.keys(unwrapSearchFieldFuncMap).includes(oneField)
        ) {
          // process wrap field
          tempResult = unwrapSearchFieldFuncMap[
            oneField as keyof typeof unwrapSearchFieldFuncMap
          ](inputValue, 'autoComplete');
        } else if (mappingFields.includes(oneField as MappingField)) {
          // process mapping fields, only push the value in existed log items
          Object.keys((quarantineL10nMap as any)[oneField]).forEach((key) => {
            if (
              logFieldArr.includes((quarantineL10nMap as any)[oneField][key]) &&
              (quarantineL10nMap as any)[oneField][key]
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            ) {
              tempResult.push((quarantineL10nMap as any)[oneField][key]);
            }
          });
        }
      });
      if (tempResult.length) {
        result = tempResult.map((item) => ({
          title: item,
          key: `${field}$$${item}`,
        }));
        setItems(result);
      } else {
        let type = 'queryString';
        if (field === 'all') {
          type = 'terms';
        }

        const params = {
          type: logType,
          timeRange,
          searches: JSON.stringify([
            {
              field,
              values: [
                ...surroundWildcardForAscii(
                  escapeQueryString(field, inputValue),
                  field
                ),
              ],
              type,
            },
          ]),
        };
        abortControllerRef.current.abort('abort previous request');
        abortControllerRef.current = new AbortController();
        try {
          const res = await LogApi.getAutoComplete(params, {
            signal: abortControllerRef.current.signal,
          });
          if (res.data[field]) {
            const items: SearchOptionItem[] = [];
            res.data[field].forEach((item) => {
              const fields = mergedSubFields.length ? mergedSubFields : [field];
              fields.forEach((oneField) => {
                const result: SearchOptionItem = {
                  title: item.key,
                  key: `${oneField}$$${item.key}`,
                };
                if (
                  wrapFields.includes(field) &&
                  Object.keys(wrapSearchFieldFuncMap).includes(field)
                ) {
                  // process wrap field
                  result.title = wrapSearchFieldFuncMap[
                    field as keyof typeof wrapSearchFieldFuncMap
                  ](item.key, quarantineL10nMap);
                } else if (mappingFields.includes(field as MappingField)) {
                  result.title = quarantineL10nMap[field][item.key];
                }
                items.push(result);
              });
            });
            setItems(items);
          } else {
            setItems([]);
          }
        } catch (error: any) {
          if (axios.isCancel(error)) {
            console.log('cancel request is ok');
          } else {
            console.error('get auto complete error:', error);
          }
        }
      }
    },
    [field]
  );
  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
  } = useCombobox({
    // onInputValueChange({ inputValue }) {
    //   setWidth(inputValue ? inputValue.length : 0);
    //   inputHandler({
    //     ...editItem,
    //     value: inputValue,
    //     focus: true,
    //   });
    //   // setItems(items.filter(getItemsFilter(inputValue)));
    // },
    onStateChange({ type, selectedItem }) {
      // console.log(
      //   'singleCriteria state change:',
      //   type,
      //   value,
      //   newInputValue,
      //   selectedItem
      // );
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.InputBlur:
          // under these senario, newInputValue will be undefined
          setFocus(false);
          onComplete();
          break;
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          // the newInputValue will be undefined
          // the multiple selection component will also hanlde the click event to change the focus status
          // wait a few millseconds to change the context
          setTimeout(() => {
            if (selectedItem) {
              // only change status when option is selected
              // if no option selected but input enter, do not change status
              inputHandler({
                ...editItem,
                value: selectedItem?.title,
                focus: false,
              });
            }
          }, 0);
          setFocus(false);
          onComplete();
          break;
        // case useCombobox.stateChangeTypes.InputChange:
        //   setWidth(newInputValue ? newInputValue.length : 0);
        //   inputHandler({
        //     ...editItem,
        //     value: newInputValue,
        //     focus: true,
        //   });
        //   autoComplete(newInputValue || '').then((r) => r);
        //   break;
        default:
          break;
      }
    },
    items,
    itemToString(item) {
      return item ? item.title : '';
    },
    stateReducer,
  });

  useEffect(() => {
    // console.log('single criteria focus change:', focus);
    setFocus(focus);
  }, [focus]);
  // console.log('SingleCriterion render:', editItem);
  return (
    <div style={style} className="comboBoxStyles">
      <div className="flex">
        <label {...getLabelProps()}>{label}&nbsp;:</label>
        <div className="flex">
          <input
            className="singleCriteriaEdit"
            type="text"
            style={{
              width: `${width + 20}px`,
              paddingLeft: '4px',
              marginLeft: '8px',
              background: colorMode === 'dark' ? '#424242' : '#f2f2f2',
            }}
            {...getInputProps({
              onFocus: (e) => {
                e.stopPropagation();
                setFocus(true);
              },
              onChange: (e: any) => {
                const newInputValue = e.target.value;
                setWidth(newInputValue ? getTextWidth(newInputValue) : 0);
                inputHandler({
                  ...editItem,
                  value: newInputValue,
                  focus: true,
                });
                autoComplete(newInputValue || '').then((r) => r);
              },
              ref: refInput,
            })}
            value={value}
            autoFocus
          />
        </div>
      </div>
      <ul {...getMenuProps()} style={menuMultipleStyles}>
        {isOpen &&
          items.map((item, index) => (
            <li
              key={`${item.key}`}
              className={
                highlightedIndex === index ? 'optionItemSelected' : 'optionItem'
              }
              {...getItemProps({ item, index })}
            >
              <span>{item.title}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}

function getTextWidth(text: string) {
  const tempSpan = document.createElement('span');
  tempSpan.style.position = 'absolute';
  tempSpan.style.visibility = 'hidden';
  tempSpan.textContent = text;
  document.body.appendChild(tempSpan);
  const textWidth = tempSpan.clientWidth;
  document.body.removeChild(tempSpan);
  return textWidth;
}

export default React.memo(ComboBoxInput);
